/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { CKBOX_VERSION, CKEDITOR5_VERSION, type EditorType } from '@ckeditor/ckeditor5-metadata';
import type { EditorPlugins } from '../../types/EditorPlugins';
import type { EditorLanguage } from '../../components/LanguageSelection/languages';
import type { InstallMethod } from '../../constants/installation';
import { groupByInstallPackage } from '../groupByInstallPackage/groupByInstallPackage';
import { areTranslationsNeeded, getTranslationImports } from '../translations/translations';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import { getStylesImport } from '../getStylesImport/getStylesImport';
import { getPluginsImport, type PluginsImportResult } from '../getPluginsImport/getPluginsImport';
import { getUsersIntegrationPlugin } from '../getUsersIntegrationPlugin/getUsersIntegrationPlugin';
import { getCommentsIntegrationPlugin } from '../getCommentsIntegrationPlugin/getCommentsIntegrationPlugin';
import { getRevisionHistoryIntegrationPlugin } from '../getRevisionHistoryIntegrationPlugin/getRevisionHistoryIntegrationPlugin';
import { getTrackChangesIntegrationPlugin } from '../getTrackChangesIntegrationPlugin/getTrackChangesIntegrationPlugin';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import { type ConfigUpdateAlert, getConfigUpdateAlert } from '../getConfigUpdateAlert/getConfigUpdateAlert';
import { getTokenVariables } from '../getTokenVariables/getTokenVariables';
import { getVueRef } from '../getVueRef/getVueRef';
import type { CustomerPortalAccount } from '../../contexts/CustomerPortalContext';
import {
	EDITOR_ANNOTATIONS_SIDEBAR_IDENTIFIER,
	EDITOR_CONTAINER_IDENTIFIER,
	EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER,
	EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER,
	EDITOR_MINIMAP_SIDEBAR_IDENTIFIER,
	EDITOR_OUTLINE_SIDEBAR_IDENTIFIER,
	EDITOR_PRESENCE_LIST_IDENTIFIER,
	EDITOR_REVISION_HISTORY_EDITOR_IDENTIFIER,
	EDITOR_REVISION_HISTORY_IDENTIFIER,
	EDITOR_REVISION_HISTORY_SIDEBAR_IDENTIFIER,
	EDITOR_WORD_COUNT
} from '../../constants/identifiers';
import { requiresMinimapContainer } from '../requiresMinimapContainer/requiresMinimapContainer';
import { requiresCommentsContainer } from '../requiresCommentsContainer/requiresCommentsContainer';
import { requiresDocumentOutlineContainer } from '../requiresDocumentOutlineContainer/requiresDocumentOutlineContainer';
import { requiresPresenceListContainer } from '../requiresPresenceListContainer/requiresPresenceListContainer';
import { requiresRevisionHistoryContainers } from '../requiresRevisionHistoryContainers/requiresRevisionHistoryContainers';
import type { EditorOptions } from '../../components/SnippetsContainer/Snippets/types';
import { requiresWordCountPlugin } from '../requiresWordCountPlugin/requiresWordCountPlugin';
import { getBuilderUrlComment } from '../getBuilderUrlComment/getBuilderUrlComment';
import { getDefaultHexColors } from '../getDefaultHexColors/getDefaultHexColors';

export type GetVueSourceTemplateArgs = {
	customerPortalAccount: CustomerPortalAccount | null;
	editorConfig: string;
	editorOptions: EditorOptions;
	evaluationLicense: string;
	htmlString: string;
	selectedInstallMethod: InstallMethod;
	builderUrl: string;
};

export function getVueSourceTemplate( {
	customerPortalAccount,
	editorConfig,
	editorOptions,
	evaluationLicense,
	htmlString,
	selectedInstallMethod,
	builderUrl
}: GetVueSourceTemplateArgs ): string {
	const { editorPlugins, toolbarOptions, editorType, editorLanguage } = editorOptions;

	const groupedPlugins = groupByInstallPackage( editorPlugins );
	const hasPremiumPlugins = groupedPlugins[ 'ckeditor5-premium-features' ].length > 0;
	const configUpdateAlert = getConfigUpdateAlert( { editorPlugins, editorConfig, configRef: 'config.value' } );
	const pluginsImport = getPluginsImport( editorType, groupedPlugins );
	const templateRefs = getTemplateRefs( editorPlugins, toolbarOptions, editorType );
	const requiresWatchEffect = !!configUpdateAlert.call;
	const requiresTemplateRef = !!templateRefs;

	return `
		<template>
			${ htmlString.replace( '__VUE_COMPONENT_PLACEHOLDER__', getVueComponent( editorType, editorPlugins ) ) }
		</template>

		<script setup>
			${ getBuilderUrlComment( builderUrl ) }

			${ getVueImports( requiresWatchEffect, requiresTemplateRef ) }
			${ getCKEditorIntegrationImports( selectedInstallMethod ) }
			${ selectedInstallMethod !== 'cdn' ? getCKEditorImportsFromPackage( pluginsImport ) : '' }

			${ selectedInstallMethod !== 'cdn' ? getTranslationImports( editorLanguage, hasPremiumPlugins ) : '' }

			${ selectedInstallMethod !== 'cdn' ? getStylesImport( selectedInstallMethod, hasPremiumPlugins ) : '' }

			${ getTokenVariables( { editorConfig, editorPlugins, customerPortalAccount, evaluationLicense, selectedInstallMethod } ) }

			${ getDefaultHexColors( editorPlugins ) }

			${ templateRefs }

			${ selectedInstallMethod !== 'cdn' ? getIntegrationPlugins( editorPlugins ) : '' }

			${ selectedInstallMethod === 'cdn' ? getCKEditorCloudComposable( editorLanguage, editorPlugins, hasPremiumPlugins ) : '' }

			const isLayoutReady = ref( false );

			${ getEditor( selectedInstallMethod, editorType ) }

			const config = computed( () => {
				if ( !isLayoutReady.value ) {
					return null;
				}

				${ selectedInstallMethod === 'cdn' ? getCloudReadyCheck() : '' }

				${ selectedInstallMethod === 'cdn' ? getCKEditorImportsFromCloud( pluginsImport, editorType ) : '' }

				${ selectedInstallMethod === 'cdn' ? getIntegrationPlugins( editorPlugins ) : '' }

				return ${ editorConfig };
			});

			onMounted( () => {
				isLayoutReady.value = true;
			} );

			${ requiresWatchEffect ? getConfigUpdateAlertCall( configUpdateAlert ) : '' }

			${ getOnReadyMethod( editorType, toolbarOptions, editorPlugins ) }

			${ configUpdateAlert.fn }
		</script>
	`;
}

function getVueImports( requiresWatchEffect: boolean, requiresTemplateRef: boolean ) {
	const imports = [
		'computed',
		'ref',
		'onMounted',
		requiresWatchEffect ? 'watchEffect' : '',
		requiresTemplateRef ? 'useTemplateRef' : ''
	].filter( Boolean );

	return `import { ${ imports } } from 'vue';`;
}

function getCKEditorIntegrationImports( selectedInstallMethod: InstallMethod ) {
	const imports = [
		'Ckeditor',
		selectedInstallMethod === 'cdn' ? 'useCKEditorCloud' : ''
	].filter( Boolean );

	return `import { ${ imports } } from '@ckeditor/ckeditor5-vue';`;
}

function getCKEditorImportsFromPackage( pluginsImportResult: PluginsImportResult ) {
	const { importsFromCKEditor, importsFromCKEditorPremiumFeatures } = pluginsImportResult;
	const hasPremiumPlugins = importsFromCKEditorPremiumFeatures.length > 0;

	return `
		import { ${ importsFromCKEditor } } from 'ckeditor5';
		${ hasPremiumPlugins ? `import { ${ importsFromCKEditorPremiumFeatures } } from 'ckeditor5-premium-features';` : '' }
	`;
}

function getCKEditorImportsFromCloud( pluginsImportResult: PluginsImportResult, editorType: EditorType ) {
	const { importsFromCKEditor, importsFromCKEditorPremiumFeatures } = pluginsImportResult;
	const importsFromCKEditorWithoutEditorType = importsFromCKEditor.filter( plugin => plugin !== editorType );
	const hasPremiumPlugins = importsFromCKEditorPremiumFeatures.length > 0;

	return `
		const { ${ importsFromCKEditorWithoutEditorType } } = cloud.data.value.CKEditor;
		${ hasPremiumPlugins ? `const { ${ importsFromCKEditorPremiumFeatures } } = cloud.data.value.CKEditorPremiumFeatures;` : '' }
	`;
}

function getTemplateRef( selector: string ) {
	const { variableName, htmlRefElement } = getVueRef( selector );

	return `const ${ variableName } = useTemplateRef( '${ htmlRefElement }' );`;
}

function getTemplateRefs( editorPlugins: EditorPlugins, toolbarOptions: ToolbarOptions, editorType: EditorType ) {
	const isDecoupled = isDecoupledEditor( editorType );
	const hasMinimap = requiresMinimapContainer( editorPlugins );
	const hasAnnotations = requiresCommentsContainer( editorPlugins );
	const hasDocumentOutline = requiresDocumentOutlineContainer( editorPlugins );
	const hasPresenceList = requiresPresenceListContainer( editorPlugins );
	const hasRevisionHistory = requiresRevisionHistoryContainers( editorPlugins );
	const hasMenuBar = toolbarOptions.isMenuBarUsed;
	const hasWordCount = requiresWordCountPlugin( editorPlugins );

	return [
		isDecoupled ? getTemplateRef( EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER ) : '',
		hasMenuBar ? getTemplateRef( EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER ) : '',
		hasMinimap ? getTemplateRef( EDITOR_MINIMAP_SIDEBAR_IDENTIFIER ) : '',
		hasAnnotations ? getTemplateRef( EDITOR_ANNOTATIONS_SIDEBAR_IDENTIFIER ) : '',
		hasPresenceList ? getTemplateRef( EDITOR_PRESENCE_LIST_IDENTIFIER ) : '',
		hasDocumentOutline ? getTemplateRef( EDITOR_OUTLINE_SIDEBAR_IDENTIFIER ) : '',
		hasRevisionHistory ? [
			getTemplateRef( EDITOR_CONTAINER_IDENTIFIER ),
			getTemplateRef( EDITOR_REVISION_HISTORY_IDENTIFIER ),
			getTemplateRef( EDITOR_REVISION_HISTORY_EDITOR_IDENTIFIER ),
			getTemplateRef( EDITOR_REVISION_HISTORY_SIDEBAR_IDENTIFIER )
		] : '',
		hasWordCount ? getTemplateRef( EDITOR_WORD_COUNT ) : ''
	].flat().filter( Boolean ).join( '\n' );
}

function getOnReadyMethod( editorType: EditorType, toolbarOptions: ToolbarOptions, editorPlugins: EditorPlugins ) {
	const hasWordCount = requiresWordCountPlugin( editorPlugins );
	const isDecoupled = isDecoupledEditor( editorType );

	if ( !isDecoupled && !hasWordCount ) {
		return '';
	}

	const { isMenuBarUsed } = toolbarOptions;
	const editorToolbar = getVueRef( EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER ).nativeElement;
	const editorMenuBar = getVueRef( EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER ).nativeElement;
	const wordCount = getVueRef( EDITOR_WORD_COUNT ).nativeElement;

	return `
		function onReady( editor ) {
			${ hasWordCount ? `[ ...${ wordCount }.children ].forEach( child => child.remove() );` : '' }
			${ isDecoupled ? `[ ...${ editorToolbar }.children ].forEach( child => child.remove() );` : '' }
			${ isMenuBarUsed ? `[ ...${ editorMenuBar }.children ].forEach( child => child.remove() );` : '' }

			${ hasWordCount ? 'const wordCount = editor.plugins.get( \'WordCount\' );' : '' }
			${ hasWordCount ? `${ wordCount }.appendChild( wordCount.wordCountContainer );` : '' }
			${ isDecoupled ? `${ editorToolbar }.appendChild( editor.ui.view.toolbar.element );` : '' }
			${ isMenuBarUsed ? `${ editorMenuBar }.appendChild( editor.ui.view.menuBarView.element );` : '' }
		}
	`;
}

function getVueComponent( editorType: EditorType, editorPlugins: EditorPlugins ) {
	return `
		<ckeditor
			v-if="editor && config"
			:modelValue="config.initialData"
			:editor="editor"
			:config="config"
			${ isDecoupledEditor( editorType ) || requiresWordCountPlugin( editorPlugins ) ? '@ready="onReady"' : '' }
		/>
	`;
}

function getCKEditorCloudComposable( editorLanguage: EditorLanguage, editorPlugins: EditorPlugins, hasPremiumPlugins: boolean ) {
	const cloudConfig: Record<string, any> = {
		version: CKEDITOR5_VERSION
	};

	if ( hasPremiumPlugins ) {
		cloudConfig.premium = true;
	}

	if ( areTranslationsNeeded( editorLanguage ) ) {
		cloudConfig.translations = [ editorLanguage ];
	}

	if ( editorPlugins.includes( 'CKBox' ) ) {
		cloudConfig.ckbox = {
			version: CKBOX_VERSION
		};
	}

	return `const cloud = useCKEditorCloud( ${ JSON.stringify( cloudConfig ) } );`;
}

function getIntegrationPlugins( editorPlugins: EditorPlugins ) {
	return `
		${ getUsersIntegrationPlugin( editorPlugins ) }

		${ getCommentsIntegrationPlugin( editorPlugins ) }

		${ getTrackChangesIntegrationPlugin( editorPlugins ) }

		${ getRevisionHistoryIntegrationPlugin( editorPlugins ) }
	`;
}

function getEditor( selectedInstallMethod: InstallMethod, editorType: EditorType ) {
	if ( selectedInstallMethod !== 'cdn' ) {
		return `const editor = ${ editorType };`;
	}

	return `
		const editor = computed( () => {
			${ getCloudReadyCheck() }

			return cloud.data.value.CKEditor.${ editorType };
		} );
	`;
}

function getConfigUpdateAlertCall( configUpdateAlert: ConfigUpdateAlert ) {
	return `
		watchEffect( () => {
			if ( config.value ) {
				${ configUpdateAlert.call }
			}
		} );
	`;
}

function getCloudReadyCheck() {
	return `
		if ( !cloud.data.value ) {
			return null;
		}
	`;
}
